import Vue from 'vue'
import router from './router'
import * as Config from './config'
import store from './store'

/**
* Auth Plugin
*
* (see https://vuejs.org/v2/guide/plugins.html for more info on Vue.js plugins)
*
* Handles login and token authentication using OAuth2.
*/
export default {

  /**
   * Install the Auth class.
   *
   * Creates a Vue-resource http interceptor to handle automatically adding auth headers
   * and refreshing tokens. Then attaches this object to the global Vue (as Vue.auth).
   *
   * @param {Object} Vue The global Vue.
   * @param {Object} options Any options we want to have in our plugin.
   * @return {void}
   */
  install (Vue, options) {
    Vue.http.interceptors.push((request, next) => {
      const token = store.state.auth.accessToken
      const hasAuthHeader = request.headers.has('Authorization')

      if (token && !hasAuthHeader) {
        this.setAuthHeader(request)
      }

      next((response) => {
        if (this._isInvalidToken(response)) {
          return this._refreshToken(request)
        }
      })
    })

    Vue.prototype.$auth = Vue.auth = this
  },

  /**
   * Login
   *
   * @param {Object.<string>} creds The username and password for logging in.
   * @param {string|null} redirect The name of the Route to redirect to.
   * @return {Promise}
   */
  login (context, creds, redirect) {
    const params = { 'ownerId': creds.ownerId, 'userId': creds.userId, 'password': creds.password }
    return Vue.http.post(Config.LOGIN_API, JSON.stringify(params))
      .then((response) => {
        context.removeLoading()
        localStorage.setItem('userProfileId', creds.userId)
        localStorage.setItem('isAdmin', 'false')
        localStorage.setItem('shareKeyBeValid', 'false')
        this._storeToken(response)
        if (redirect) {
          router.push({ name: redirect })
        }
      })
      .catch((error) => {
        context.removeLoading()
        if (error.body) {
          switch (error.status) {
            case 400:
              context.error = '事業者ID、ユーザーIDもしくはパスワードが間違っています。'
              break
            case 404:
              context.error = '事業者ID、ユーザーIDもしくはパスワードが間違っています。'
              break
            case 500:
              context.error = 'サーバーから応答がありませんでした。しばらく経ってからやり直してください。'
              break
            case 503:
              context.error = 'サーバーから応答がありませんでした。しばらく経ってからやり直してください。'
              break
            default:
              context.error = '事業者ID、ユーザーIDもしくはパスワードが間違っています。'
              break
          }
        } else {
          context.error = 'インターネットに接続されていません'
        }
      })
  },

  /**
   * Logout
   *
   * Clear all data in our Vuex store (which resets logged-in status) and redirect back
   * to login form.
   *
   * @return {void}
   */
  logout () {
    localStorage.clear()
    store.commit('CLEAR_ALL_DATA')
    router.push({ name: 'login' })
  },

  /**
   * Set the Authorization header on a Vue-resource Request.
   *
   * @param {Request} request The Vue-Resource Request instance to set the header on.
   * @return {void}
   */
  setAuthHeader (request) {
    // このLineがないとAPIを叩くことができない。
    request.headers.set('Authorization', 'Basic Og==')
    request.headers.set('x-id-token', store.state.auth.accessToken)
  },

  /**
   * Retry the original request.
   *
   * Let's retry the user's original target request that had recieved a invalid token response
   * (which we fixed with a token refresh).
   *
   * @param {Request} request The Vue-resource Request instance to use to repeat an http call.
   * @return {Promise}
   */
  _retry (request) {
    this.setAuthHeader(request)

    return Vue.http(request)
      .then((response) => {
        return response
      })
      .catch((response) => {
        return response
      })
  },

  /**
   * Refresh the access token
   *
   * Make an ajax call to the OAuth2 server to refresh the access token (using our refresh token).
   *
   * @private
   * @param {Request} request Vue-resource Request instance, the original request that we'll retry.
   * @return {Promise}
   */
  _refreshToken (request) {
    const params = { 'refreshToken': store.state.auth.refreshToken }

    return Vue.http.post(Config.REFRESH_TOKEN_API, params)
      .then((result) => {
        this._storeToken(result)
        return this._retry(request)
      })
      .catch((errorResponse) => {
        this.logout()
      })
  },

  /**
   * Store tokens
   *
   * Update the Vuex store with the access/refresh tokens received from the response from
   * the Oauth2 server.
   *
   * @private
   * @param {Response} response Vue-resource Response instance from an OAuth2 server.
   *      that contains our tokens.
   * @return {void}
   */
  _storeToken (response) {
    const auth = store.state.auth
    const user = store.state.user

    auth.isLoggedIn = true
    auth.accessToken = response.body.idToken
    auth.refreshToken = response.body.refreshToken

    store.commit('UPDATE_AUTH', auth)
    store.commit('UPDATE_USER', user)
  },

  /**
   * Check if the Vue-resource Response is an invalid token response.
   *
   * @private
   * @param {Response} response The Vue-resource Response instance received from an http call.
   * @return {boolean}
   */
  _isInvalidToken (response) {
    const status = response.status
    const error = response.body.message

    return (status === 401 && (error === 'The incoming token has expired'))
  }
}



// WEBPACK FOOTER //
// ./src/auth.js